import { useQuery } from '@tanstack/react-query';
import type {
    DataTag,
    DefinedInitialDataOptions,
    DefinedUseQueryResult,
    QueryClient,
    QueryFunction,
    QueryKey,
    UndefinedInitialDataOptions,
    UseQueryOptions,
    UseQueryResult,
} from '@tanstack/react-query';

import { useCallback } from 'react';

import type {
    ProblemDetails,
    ResolvePrincipalsParams,
    ResolvePrincipalsResponse,
    SearchPrincipalsParams,
    SearchPrincipalsResponse,
} from '../models';

import { useCustomIDSClient } from '../../custom-client';
import type { ErrorType } from '../../custom-client';

/**
 * Resolves principal ids to its corresponding models<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.read<br>login.umdaschgroup.com/clients.read<br>login.umdaschgroup.com/groups.read<br>login.umdaschgroup.com/tenants.read
 */
export const useResolvePrincipalsHook = () => {
    const resolvePrincipals = useCustomIDSClient<ResolvePrincipalsResponse>();

    return useCallback(
        (params: ResolvePrincipalsParams, signal?: AbortSignal) => {
            return resolvePrincipals({ url: `/principals/resolve`, method: 'GET', params, signal });
        },
        [resolvePrincipals],
    );
};

export const getResolvePrincipalsQueryKey = (params?: ResolvePrincipalsParams) => {
    return [`/principals/resolve`, ...(params ? [params] : [])] as const;
};

export const useResolvePrincipalsQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useResolvePrincipalsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: ResolvePrincipalsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useResolvePrincipalsHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getResolvePrincipalsQueryKey(params);

    const resolvePrincipals = useResolvePrincipalsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useResolvePrincipalsHook>>>> = ({ signal }) =>
        resolvePrincipals(params, signal);

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useResolvePrincipalsHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ResolvePrincipalsQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useResolvePrincipalsHook>>>
>;
export type ResolvePrincipalsQueryError = ErrorType<ProblemDetails>;

export function useResolvePrincipals<
    TData = Awaited<ReturnType<ReturnType<typeof useResolvePrincipalsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: ResolvePrincipalsParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useResolvePrincipalsHook>>>, TError, TData>>
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useResolvePrincipalsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useResolvePrincipalsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useResolvePrincipals<
    TData = Awaited<ReturnType<ReturnType<typeof useResolvePrincipalsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: ResolvePrincipalsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useResolvePrincipalsHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useResolvePrincipalsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useResolvePrincipalsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useResolvePrincipals<
    TData = Awaited<ReturnType<ReturnType<typeof useResolvePrincipalsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: ResolvePrincipalsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useResolvePrincipalsHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useResolvePrincipals<
    TData = Awaited<ReturnType<ReturnType<typeof useResolvePrincipalsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: ResolvePrincipalsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useResolvePrincipalsHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useResolvePrincipalsQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Searches principals<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.read<br>login.umdaschgroup.com/clients.read<br>login.umdaschgroup.com/groups.read<br>login.umdaschgroup.com/tenants.read
 */
export const useSearchPrincipalsHook = () => {
    const searchPrincipals = useCustomIDSClient<SearchPrincipalsResponse>();

    return useCallback(
        (params: SearchPrincipalsParams, signal?: AbortSignal) => {
            return searchPrincipals({ url: `/principals/search`, method: 'GET', params, signal });
        },
        [searchPrincipals],
    );
};

export const getSearchPrincipalsQueryKey = (params?: SearchPrincipalsParams) => {
    return [`/principals/search`, ...(params ? [params] : [])] as const;
};

export const useSearchPrincipalsQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useSearchPrincipalsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: SearchPrincipalsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useSearchPrincipalsHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getSearchPrincipalsQueryKey(params);

    const searchPrincipals = useSearchPrincipalsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useSearchPrincipalsHook>>>> = ({ signal }) =>
        searchPrincipals(params, signal);

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useSearchPrincipalsHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type SearchPrincipalsQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useSearchPrincipalsHook>>>>;
export type SearchPrincipalsQueryError = ErrorType<ProblemDetails>;

export function useSearchPrincipals<
    TData = Awaited<ReturnType<ReturnType<typeof useSearchPrincipalsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: SearchPrincipalsParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useSearchPrincipalsHook>>>, TError, TData>>
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useSearchPrincipalsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useSearchPrincipalsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSearchPrincipals<
    TData = Awaited<ReturnType<ReturnType<typeof useSearchPrincipalsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: SearchPrincipalsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useSearchPrincipalsHook>>>, TError, TData>>
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useSearchPrincipalsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useSearchPrincipalsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSearchPrincipals<
    TData = Awaited<ReturnType<ReturnType<typeof useSearchPrincipalsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: SearchPrincipalsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useSearchPrincipalsHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useSearchPrincipals<
    TData = Awaited<ReturnType<ReturnType<typeof useSearchPrincipalsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: SearchPrincipalsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useSearchPrincipalsHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useSearchPrincipalsQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}
